import React, { useState, useEffect } from "react";
import { XCircle } from "react-feather";

import styles from "./modal.module.css";

interface ModalProps {
    children?: React.ReactNode;
    className?: string;
    visible?: boolean;
    title?: string;
    subtitle?: string;
    isLoading?: boolean;
    showHeader?: boolean;
    onDismiss?: () => void;
    onRequestClose?: () => void;
    onVisibilityChange?: (visible: boolean) => void;
    contentRef?: React.RefObject<HTMLDivElement>;
}

const Modal: React.FunctionComponent<ModalProps> = ({
    children,
    title,
    subtitle,
    className,
    visible = false,
    isLoading = false,
    showHeader = true,
    onDismiss,
    onRequestClose,
    onVisibilityChange,
    contentRef,
}: ModalProps) => {
    // States

    const [_visible, _setVisible] = useState(visible);

    // Effects

    useEffect(() => {
        if (onVisibilityChange) {
            onVisibilityChange(_visible);
        }
    }, [_visible]);

    useEffect(() => {
        _setVisible(visible);
    }, [visible]);

    const handleCloseRequest = () => {
        if (isLoading) {
            return;
        }

        if (onRequestClose) {
            onRequestClose();
            return;
        }

        _setVisible(false);
    };

    // Rendering

    let rootClass = styles.modal;
    if (className) rootClass += ` ${className}`;

    return (
        <div
            className={`${styles.background} ${_visible ? styles.visible : ""}`}
            onClick={handleCloseRequest}
        >
            <div className={rootClass} onClick={(e) => e.stopPropagation()}>
                {showHeader && (
                    <div className={styles.header}>
                        <div className="d-flex flex-column">
                            <span>{title}</span>
                            {subtitle && <span>{subtitle}</span>}
                        </div>
                        <button
                            onClick={() => {
                                if (onRequestClose) {
                                    onRequestClose();
                                } else {
                                    _setVisible(false);
                                }
                                if (onDismiss) {
                                    onDismiss();
                                }
                            }}
                            disabled={isLoading}
                        >
                            <XCircle />
                        </button>
                    </div>
                )}
                <div className={styles.content} ref={contentRef}>
                    {children}
                </div>
            </div>
        </div>
    );
};

export default Modal;
